import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';

const MAX_CACHED_ROUTES = 12;

const getRouteKey = (location) => `${location.pathname}${location.search}${location.hash}`;

export default function PersistentOutlet() {
  const location = useLocation();
  const outlet = useOutlet();
  const [cachedOutlets, setCachedOutlets] = useState(new Map());
  const routeKey = useMemo(() => getRouteKey(location), [location]);

  useEffect(() => {
    setCachedOutlets((prev) => {
      const next = new Map(prev);
      const existingEntry = next.get(routeKey);
      const mountedAt = existingEntry?.mountedAt ?? Date.now();

      next.set(routeKey, {
        element: existingEntry?.element ?? outlet,
        mountedAt,
        lastActiveAt: Date.now(),
      });

      if (next.size <= MAX_CACHED_ROUTES) {
        return next;
      }

      const inactiveEntries = Array.from(next.entries())
        .filter(([key]) => key !== routeKey)
        .sort((a, b) => a[1].lastActiveAt - b[1].lastActiveAt);

      while (next.size > MAX_CACHED_ROUTES && inactiveEntries.length > 0) {
        const [oldestKey] = inactiveEntries.shift();
        next.delete(oldestKey);
      }

      return next;
    });
  }, [outlet, routeKey]);

  return (
    <>
      {Array.from(cachedOutlets.entries()).map(([key, entry]) => {
        return (
          <div
            key={key}
            style={{ display: key === routeKey ? 'block' : 'none', height: '100%' }}
            aria-hidden={key === routeKey ? undefined : 'true'}
          >
            {entry.element}
          </div>
        );
      })}

      {!cachedOutlets.has(routeKey) ? (
        <div key={routeKey} style={{ height: '100%' }}>
          {outlet}
        </div>
      ) : null}
    </>
  );
}
